import axios from 'axios';
import { sanitizeQueryParams } from '../utils/secureApi';
import { API_CONFIG } from '../config/constants';

// Use environment variable or fallback to localhost
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost/shopping_cart/shopping-cart-api/public/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: API_CONFIG.TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-Requested-With': 'XMLHttpRequest', // Security: Identify as AJAX request
  },
  withCredentials: false, // Set to true if using httpOnly cookies
});

// Request interceptor to add token and sanitize query parameters
api.interceptors.request.use(
  (config) => {
    // Add authentication token
    const token = localStorage.getItem('admin_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Sanitize query parameters to prevent injection attacks
    if (config.params && typeof config.params === 'object') {
      config.params = sanitizeQueryParams(config.params);
    }

    // Add timestamp to prevent caching sensitive requests (optional)
    if (config.method === 'get' && !config.params) {
      config.params = {};
    }

    // Log request for debugging (remove in production or add environment check)
    if (process.env.NODE_ENV === 'development') {
      console.debug('API Request:', {
        method: config.method,
        url: config.url,
        params: config.params
      });
    }

    return config;
  },
  (error) => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors and security
api.interceptors.response.use(
  (response) => {
    // Log response for debugging (remove in production or add environment check)
    if (process.env.NODE_ENV === 'development') {
      console.debug('API Response:', {
        status: response.status,
        url: response.config.url
      });
    }

    return response;
  },
  (error) => {
    // Handle authentication errors
    if (error.response?.status === 401) {
      // Clear auth data
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_user');

      // Redirect to login if not already there
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }

    // Handle forbidden access (403)
    if (error.response?.status === 403) {
      console.error('Access forbidden:', error.response.data?.message);
      // Optionally redirect to unauthorized page
      // window.location.href = '/unauthorized';
    }

    // Handle CSRF token errors (419 in Laravel)
    if (error.response?.status === 419) {
      console.error('CSRF token mismatch. Please refresh and try again.');
      // Could trigger a token refresh here
    }

    // Handle rate limiting (429)
    if (error.response?.status === 429) {
      console.error('Too many requests. Please slow down.');
    }

    // Log error for debugging
    if (process.env.NODE_ENV === 'development') {
      console.error('API Error:', {
        status: error.response?.status,
        message: error.response?.data?.message,
        url: error.config?.url
      });
    }

    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (credentials) => api.post('/admin/login', credentials),
  logout: () => api.post('/admin/logout'),
  profile: () => api.get('/admin/profile'),
  refreshToken: () => api.post('/admin/refresh-token'),
};

// Categories API
export const categoriesAPI = {
  getAll: (params) => api.get('/admin/categories', { params }),
  getById: (id) => api.get(`/admin/categories/${id}`),
  create: (data) => api.post('/admin/categories', data),
  update: (id, data) => api.put(`/admin/categories/${id}`, data),
  delete: (id) => api.delete(`/admin/categories/${id}`),
  toggleActive: (id) => api.post(`/admin/categories/${id}/toggle-active`),
};

// Images API
export const imagesAPI = {
  uploadCategoryImage: (formData) => api.post('/admin/images/categories/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  listCategoryImages: (params) => api.get('/admin/images/categories/list', { params }),
  deleteImage: (data) => api.post('/admin/images/delete', data),
};

// Features API
export const featuresAPI = {
  getAll: (params) => api.get('/admin/features', { params }),
  getById: (id) => api.get(`/admin/features/${id}`),
  getByCategory: (categoryId) => api.get(`/admin/categories/${categoryId}/features`),
  create: (data) => api.post('/admin/features', data),
  update: (id, data) => api.put(`/admin/features/${id}`, data),
  delete: (id) => api.delete(`/admin/features/${id}`),
};

// Brands API
export const brandsAPI = {
  getAll: (params) => api.get('/admin/brands', { params }),
  getById: (id) => api.get(`/admin/brands/${id}`),
  getByCategory: (categoryId) => api.get(`/admin/categories/${categoryId}/brands`),
  getCategories: () => api.get('/admin/brands/categories/list'),
  create: (data) => api.post('/admin/brands', data),
  update: (id, data) => api.put(`/admin/brands/${id}`, data),
  delete: (id) => api.delete(`/admin/brands/${id}`),
  toggleActive: (id) => api.post(`/admin/brands/${id}/toggle-active`),
};

// Feature Values API
export const featureValuesAPI = {
  getAll: (featureId, params) => api.get(`/admin/features/${featureId}/values`, { params }),
  getById: (featureId, id) => api.get(`/admin/features/${featureId}/values/${id}`),
  getValuesList: (featureId) => api.get(`/admin/features/${featureId}/values-list`),
  create: (featureId, data) => api.post(`/admin/features/${featureId}/values`, data),
  update: (featureId, id, data) => api.put(`/admin/features/${featureId}/values/${id}`, data),
  delete: (featureId, id) => api.delete(`/admin/features/${featureId}/values/${id}`),
};

// Products API
export const productAPI = {
  // Product CRUD
  getAll: (params = {}) => api.get('/admin/products', { params }),
  getById: (id) => api.get(`/admin/products/${id}`),
  create: (data) => api.post('/admin/products', data),
  update: (id, data) => api.put(`/admin/products/${id}`, data),
  delete: (id) => api.delete(`/admin/products/${id}`),
  toggleActive: (id) => api.post(`/admin/products/${id}/toggle-active`),
  getDropdownData: () => api.get('/admin/products/dropdown-data'),

  // Product Inventory
  getInventory: (productId) => api.get(`/admin/products/${productId}/inventory`),
  createInventory: (productId, data) => api.post(`/admin/products/${productId}/inventory`, data),
  updateInventory: (productId, data) => api.put(`/admin/products/${productId}/inventory`, data),
  adjustStock: (productId, data) => api.post(`/admin/products/${productId}/inventory/adjust`, data),

  // Product Pricing
  getPricing: (productId) => api.get(`/admin/products/${productId}/pricing`),
  createPricing: (productId, data) => api.post(`/admin/products/${productId}/pricing`, data),
  updatePricing: (productId, priceId, data) => {
    const url = priceId 
      ? `/admin/products/${productId}/pricing/${priceId}` 
      : `/admin/products/${productId}/pricing`;
    return api.put(url, data);
  },
  bulkUpdatePricing: (productId, data) => api.post(`/admin/products/${productId}/pricing/bulk-update`, data),
  deletePricing: (productId, priceId) => api.delete(`/admin/products/${productId}/pricing/${priceId}`),

  // Product Images
  getImages: (productId) => api.get(`/admin/products/${productId}/images`),
  uploadImages: (productId, formData) => api.post(`/admin/products/${productId}/images`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  updateImage: (productId, imageId, data) => api.put(`/admin/products/${productId}/images/${imageId}`, data),
  deleteImage: (productId, imageId) => api.delete(`/admin/products/${productId}/images/${imageId}`),
  reorderImages: (productId, data) => api.post(`/admin/products/${productId}/images/reorder`, data),
  setPrimaryImage: (productId, imageId) => api.post(`/admin/products/${productId}/images/${imageId}/set-primary`),

  // Product Features
  getProductFeatures: (productId) => api.get(`/admin/products/${productId}/features`),
  bulkAssignFeatures: (productId, data) => api.post(`/admin/products/${productId}/features/bulk-assign`, data),
  removeFeatureAssignment: (productId, assignmentId) => api.delete(`/admin/products/${productId}/features/${assignmentId}`),

  // Product Feature Combinations
  getCombinations: (productId) => api.get(`/admin/products/${productId}/combinations`),
  getCombination: (productId, combinationId) => api.get(`/admin/products/${productId}/combinations/${combinationId}`),
  getAvailableFeatures: (productId) => api.get(`/admin/products/${productId}/available-features`),
  createCombination: (productId, data) => api.post(`/admin/products/${productId}/combinations`, data),
  updateCombination: (productId, combinationId, data) => api.put(`/admin/products/${productId}/combinations/${combinationId}`, data),
  deleteCombination: (productId, combinationId) => api.delete(`/admin/products/${productId}/combinations/${combinationId}`),

  // Product Combination Inventory
  getCombinationInventory: (productId, combinationId) => api.get(`/admin/products/${productId}/combinations/${combinationId}/inventory`),
  updateCombinationInventory: (productId, combinationId, data) => api.put(`/admin/products/${productId}/combinations/${combinationId}/inventory`, data),

  // Product Combination Pricing  
  getCombinationPricing: (productId, combinationId) => api.get(`/admin/products/${productId}/combinations/${combinationId}/pricing`),
  updateCombinationPricing: (productId, combinationId, data) => api.put(`/admin/products/${productId}/combinations/${combinationId}/pricing`, data),

  // Product Combination Images
  getCombinationImages: (productId, combinationId) => api.get(`/admin/products/${productId}/combinations/${combinationId}/images`),
  uploadCombinationImages: (productId, combinationId, formData) => api.post(`/admin/products/${productId}/combinations/${combinationId}/images`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  updateCombinationImage: (productId, combinationId, imageId, data) => api.put(`/admin/products/${productId}/combinations/${combinationId}/images/${imageId}`, data),
  deleteCombinationImage: (productId, combinationId, imageId) => api.delete(`/admin/products/${productId}/combinations/${combinationId}/images/${imageId}`),
  setCombinationPrimaryImage: (productId, combinationId, imageId) => api.post(`/admin/products/${productId}/combinations/${combinationId}/images/${imageId}/set-primary`),

  // Product Tax
  getTax: (productId) => api.get(`/admin/products/${productId}/tax`),
  saveTax: (productId, data) => api.post(`/admin/products/${productId}/tax`, data),
  updateTax: (productId, data) => api.put(`/admin/products/${productId}/tax`, data),
  deleteTax: (productId) => api.delete(`/admin/products/${productId}/tax`)
};

// User API
export const userAPI = {
  getUsers: (params) => api.get('/admin/users', { params }),
  getUser: (id) => api.get(`/admin/users/${id}`),
  createUser: (data) => api.post('/admin/users', data),
  updateUser: (id, data) => api.put(`/admin/users/${id}`, data),
  deleteUser: (id) => api.delete(`/admin/users/${id}`),
  suspendUser: (id) => api.post(`/admin/users/${id}/suspend`),
  activateUser: (id) => api.post(`/admin/users/${id}/activate`),
  getUserOrders: (id, params) => api.get(`/admin/users/${id}/orders`, { params }),
  getUserAddresses: (id) => api.get(`/admin/users/${id}/addresses`),
  getUserStats: (id) => api.get(`/admin/users/${id}/stats`),
  getUserRewardHistory: (id, params) => api.get(`/admin/users/${id}/rewards/history`, { params }),
  getUsersWithRewards: (params) => api.get('/admin/users-with-rewards', { params })
};

// Order API
export const orderAPI = {
  getOrders: (params) => api.get('/admin/orders', { params }),
  getOrder: (id) => api.get(`/admin/orders/${id}`),
  updateOrder: (id, data) => api.put(`/admin/orders/${id}`, data),
  updateOrderStatus: (id, data) => api.post(`/admin/orders/${id}/status`, data),
  confirmOrder: (id) => api.post(`/admin/orders/${id}/confirm`),
  cancelOrder: (id, data) => api.post(`/admin/orders/${id}/cancel`, data),
  markAsShipped: (id, data) => api.post(`/admin/orders/${id}/mark-shipped`, data),
  markAsDelivered: (id, data) => api.post(`/admin/orders/${id}/mark-delivered`, data),
  getOrderStats: () => api.get('/admin/orders/stats')
};

// Settings API
export const settingsAPI = {
  getSettings: (params) => api.get('/admin/settings', { params }),
  getSettingsByCategory: (category) => api.get(`/admin/settings/category/${category}`),
  getSetting: (key) => api.get(`/admin/settings/key/${key}`),
  saveSetting: (data) => api.post('/admin/settings', data),
  bulkUpdateSettings: (data) => api.post('/admin/settings/bulk-update', data),
  deleteSetting: (key) => api.delete(`/admin/settings/key/${key}`),
  initializeDefaults: () => api.post('/admin/settings/initialize-defaults')
};

// Website Images API
export const websiteImagesAPI = {
  getAll: (params) => api.get('/admin/website-images', { params }),
  getById: (id) => api.get(`/admin/website-images/${id}`),
  create: (formData) => api.post('/admin/website-images', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  update: (id, data) => api.put(`/admin/website-images/${id}`, data),
  delete: (id) => api.delete(`/admin/website-images/${id}`),
  reorder: (data) => api.post('/admin/website-images/reorder', data),
  toggleActive: (id) => api.post(`/admin/website-images/${id}/toggle-active`)
};

// Business Tax API
export const businessTaxAPI = {
  getAll: (params) => api.get('/admin/business-taxes', { params }),
  getActive: () => api.get('/admin/business-taxes/active'),
  getById: (id) => api.get(`/admin/business-taxes/${id}`),
  create: (data) => api.post('/admin/business-taxes', data),
  update: (id, data) => api.put(`/admin/business-taxes/${id}`, data),
  delete: (id) => api.delete(`/admin/business-taxes/${id}`),
  
  // Tax Percentages
  getTaxPercentages: (businessTaxId, params) => api.get(`/admin/business-taxes/${businessTaxId}/tax-percentages`, { params }),
  getTaxPercentage: (businessTaxId, id) => api.get(`/admin/business-taxes/${businessTaxId}/tax-percentages/${id}`),
  createTaxPercentage: (businessTaxId, data) => api.post(`/admin/business-taxes/${businessTaxId}/tax-percentages`, data),
  updateTaxPercentage: (businessTaxId, id, data) => api.put(`/admin/business-taxes/${businessTaxId}/tax-percentages/${id}`, data),
  deleteTaxPercentage: (businessTaxId, id) => api.delete(`/admin/business-taxes/${businessTaxId}/tax-percentages/${id}`)
};

// Coupons API
export const couponsAPI = {
  getAll: (params) => api.get('/admin/coupons', { params }),
  getById: (id) => api.get(`/admin/coupons/${id}`),
  create: (data) => api.post('/admin/coupons', data),
  update: (id, data) => api.put(`/admin/coupons/${id}`, data),
  delete: (id) => api.delete(`/admin/coupons/${id}`),
  toggleActive: (id) => api.post(`/admin/coupons/${id}/toggle-active`),
  getAnalytics: (params) => api.get('/admin/coupons/analytics', { params }),
  getUsageStats: (id) => api.get(`/admin/coupons/${id}/usage-stats`)
};

// Order Charges API
export const orderChargesAPI = {
  getAll: (params) => api.get('/admin/order-charges', { params }),
  getById: (id) => api.get(`/admin/order-charges/${id}`),
  create: (data) => api.post('/admin/order-charges', data),
  update: (id, data) => api.put(`/admin/order-charges/${id}`, data),
  delete: (id) => api.delete(`/admin/order-charges/${id}`),
  toggleActive: (id) => api.post(`/admin/order-charges/${id}/toggle-active`),
  getBusinessTaxes: () => api.get('/admin/order-charges/business-taxes'),
  calculateCharges: (data) => api.post('/admin/order-charges/calculate', data)
};

// Company Tax Registration API
export const companyTaxRegistrationAPI = {
  getAll: () => api.get('/admin/company-tax-registrations'),
  getById: (id) => api.get(`/admin/company-tax-registrations/${id}`),
  getActive: () => api.get('/admin/company-tax-registrations/active'),
  create: (data) => api.post('/admin/company-tax-registrations', data),
  update: (id, data) => api.put(`/admin/company-tax-registrations/${id}`, data),
  delete: (id) => api.delete(`/admin/company-tax-registrations/${id}`)
};

// GST Order Config API
export const gstOrderConfigAPI = {
  get: () => api.get('/admin/gst-order-config'),
  getActive: () => api.get('/admin/gst-order-config/active'),
  create: (data) => api.post('/admin/gst-order-config', data),
  update: (id, data) => api.put(`/admin/gst-order-config/${id}`, data),
  toggle: (enabled) => api.post('/admin/gst-order-config/toggle', { enabled })
};

export { api };
export default api;